import { Helmet } from 'react-helmet-async';
import CinematicHero from '../components/CinematicHero';
import RoleSelector from '../components/RoleSelector';
import Footer from '../components/Footer';
import { Recycle, Waves, Leaf, BarChart2 } from 'lucide-react';

const HIGHLIGHTS = [
  { icon: Recycle, label: 'Recogida de residuos', desc: 'Servicio diario y selectivo en todo el municipio.' },
  { icon: Waves, label: 'Limpieza de playas', desc: 'Mantenimiento de las playas del Mar Menor.' },
  { icon: Leaf, label: 'Punto Limpio (Ecoparque)', desc: 'Gestión sostenible de residuos voluminosos y especiales.' },
  { icon: BarChart2, label: '10 indicadores de calidad', desc: 'Seguimiento contractual y transparencia total.' },
];

// Imágenes reales del Mar Menor y entorno de Los Alcázares
const GALLERY = [
  {
    url: 'https://images.unsplash.com/photo-1737781371561-66630b3402de?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200',
    alt: 'Vista aérea del litoral mediterráneo al atardecer — Mar Menor, Murcia',
    caption: 'El litoral del Mar Menor',
  },
  {
    url: 'https://images.pexels.com/photos/22941535/pexels-photo-22941535.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260',
    alt: 'Atardecer rosa sobre el Mar Menor, La Manga del Mar Menor, Murcia',
    caption: 'Atardecer en La Manga',
  },
  {
    url: 'https://images.pexels.com/photos/22941533/pexels-photo-22941533.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260',
    alt: 'Vista aérea del Mar Menor desde La Manga — aguas turquesas mediterráneas',
    caption: 'Aguas del Mar Menor',
  },
];

export default function Home() {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Cuidemos Los Alcázares® | Servicios de limpieza y recogida</title>
        <meta
          name="description"
          content="Portal digital del contrato municipal de recogida de residuos, limpieza viaria, mantenimiento de playas y Ecoparque en Los Alcázares (Murcia)."
        />
        <meta property="og:title" content="Cuidemos Los Alcázares®" />
        <meta
          property="og:description"
          content="Información y acceso por rol: gestión, técnicos municipales y ciudadanía."
        />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Cinematic hero with video */}
      <CinematicHero />

      {/* Service highlights strip */}
      <section
        id="servicio"
        className="py-20 px-6 bg-white"
        aria-labelledby="servicio-heading"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase bg-[#CFE3F5] text-[#1E5AA8] mb-4">
              El servicio
            </span>
            <h2
              id="servicio-heading"
              className="font-display text-4xl sm:text-5xl text-[#0A2540] mt-3"
            >
              Un contrato, cuatro servicios
            </h2>
            <p className="mt-4 text-[#6F8AA8] text-base max-w-xl mx-auto leading-relaxed">
              Valoriza gestiona en Los Alcázares un contrato integral que cubre desde la recogida
              de basuras hasta la limpieza de playas y el Ecoparque municipal.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {HIGHLIGHTS.map(({ icon: Icon, label, desc }) => (
              <div
                key={label}
                className="rounded-2xl border border-[#CFE3F5] bg-[#FAFCFE] p-6 hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 rounded-xl bg-[#CFE3F5] flex items-center justify-center mb-4">
                  <Icon size={22} className="text-[#1E5AA8]" aria-hidden="true" />
                </div>
                <h3 className="font-semibold text-[#0A2540] text-base mb-2">{label}</h3>
                <p className="text-sm text-[#6F8AA8] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Galería fotográfica Los Alcázares / Mar Menor ── */}
      <section
        className="py-4 px-4 bg-[#FAFCFE]"
        aria-label="Imágenes de Los Alcázares y el Mar Menor"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 h-72 md:h-96">
            {GALLERY.map(({ url, alt, caption }, i) => (
              <figure
                key={i}
                className={`relative overflow-hidden rounded-2xl ${i === 0 ? 'md:col-span-1' : ''}`}
              >
                <img
                  src={url}
                  alt={alt}
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
                  loading="lazy"
                />
                {/* Caption overlay */}
                <figcaption className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[rgba(10,37,64,0.75)] to-transparent px-4 py-4 translate-y-full group-hover:translate-y-0 transition-transform">
                  <p className="text-white text-xs font-medium opacity-90">{caption}</p>
                </figcaption>
                <div className="absolute inset-0 bg-gradient-to-t from-[rgba(10,37,64,0.4)] via-transparent to-transparent pointer-events-none" aria-hidden="true" />
                <p className="absolute bottom-3 left-4 text-white text-xs font-medium drop-shadow">{caption}</p>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Role selector */}
      <RoleSelector />

      {/* Stats section */}
      <section className="py-20 px-6 bg-[#0A2540]" aria-labelledby="stats-heading">
        <div className="max-w-7xl mx-auto">
          <h2 id="stats-heading" className="sr-only">Datos del servicio</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { value: '24.000', label: 'Habitantes atendidos' },
              { value: '1.261', label: 'Contenedores con TAG RFID' },
              { value: '2.950', label: 'Tarjetas NFC ciudadanas' },
              { value: '10', label: 'Indicadores de calidad' },
            ].map(({ value, label }) => (
              <div key={label}>
                <p className="font-display text-4xl sm:text-5xl text-white mb-2">{value}</p>
                <p className="text-sm text-[#7DB9E8] leading-tight">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
